import { ServiceLayer } from "./ServiceLayer.js";
import { FeatureLayerUtils } from "./utils/FeatureLayerUtils.js";

/**
 * WFS Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * 
 * @memberof vef.map.layer
 */
export class WFSLayer extends ServiceLayer {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // init default values and define getters and setters
        this.addDefaults_({
            opacityRatio: 0.75,
            // overwrite existing defaults
            availableCrs: FeatureLayerUtils.availableCrs,
            opacitySettings: true,
            icon: "fas fa-draw-polygon"
        });

        this.setAll_("type", "wfs");

        // ignore availableCrs and always use the default value
        if ("availableCrs" in this.config) delete this.config.availableCrs;
        if ("availableCrs" in this.cache) delete this.cache.availableCrs;

        // currently active geoJSON response
        this.geoJSON = null;

        // current url and requests for the data
        this.activeUrl_ = null;
        this.activeRequest_ = null;

        // storing an array of clicked features for getFeatureInfo
        this.clickedFeatures_ = [];
    }

    /**
     * Creates URL for getting wfs layer
     */
    createUrl_() {
        const url = this.serviceUrl;
        const software = this.serviceSoftware.toLowerCase();

        let params = {
            service: this.type,
            version: (software == "esri") ? '2.0.0' : this.serviceVersion,
            request: 'GetFeature',
            typeName: this.getRequestName(),
            outputFormat: (software == "esri") ? 'GEOJSON' : 'application/json',
            SrsName: 'EPSG:4326'
        };

        // apply the filter
        if (this.filter) Object.assign(params, this.createFilterParams_(this.filter));
        return url + L.Util.getParamString(params);
    }

    /**
     * fix invalid GeoJson as a workaround for invalid data from the WFS
     * 
     * @private
     */
    fixGeoJson_(text) {
        // Workaround for ESRI LineString syntax in 10.7
        // Also remove features with coordinates as nan
        // by replacing nan with null and then removing them in the next step
        // ToDo: Remove after updating to 10.8
        if (this.serviceSoftware.toLowerCase() == "esri") {
            text = text.replaceAll('"LineString"', '"MultiLineString"');
            text = text.replace(/\bnan\b/g, null);

            let parsedJson = JSON.parse(text);

            for (let i = parsedJson.features.length - 1; i >= 0; i--) {
                if (parsedJson.features[i].geometry.coordinates.includes(null)) {
                    parsedJson.features.splice(i, 1);
                }
            }
            return parsedJson;
        } else return JSON.parse(text);
    }

    /**
     * Create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type) {
        switch (type) {
            case "leaflet":
                const layer = L.geoJSON(null, {
                    onEachFeature: (feature, layer) => {
                        if (feature.properties) {
                            layer.on("click", e => {
                                this.clickedFeatures_.push(feature);
                            });
                        }
                    },
                    pointToLayer: (geoJsonPoint, latlng) => {
                        return L.circleMarker(latlng, { renderer: FeatureLayerUtils.getCanvasRenderer() });
                    },
                    style: () => {
                        return {
                            opacity: this.opacity,
                            fillOpacity: this.opacity * this.opacityRatio,
                            renderer: FeatureLayerUtils.getCanvasRenderer()
                        }
                    }
                })

                this.mapLayers_[type] = layer;
                return layer;
        }
        return null;
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        const previousType = this.activeMap_;
        this.activeMap_ = type;

        const url = this.createUrl_();
        if (this.activeUrl_ != url) {
            this.loadGeojson_(url);
        } else if (previousType != type) {
            this.applyGeoJson_();
        } else if (Number.isFinite(this.opacity)) {
            // only apply opacity, if
            this.setOpacity(this.opacity);
        }
    }

    /**
     * load the geojson content of the layer and insert it
     * 
     * @private
     */
    loadGeojson_(url) {
        if (this.activeRequest_) {
            this.activeRequest_.abort();
            this.activeRequest_ = null;
            this.fire("load");
        }

        this.fire("loading");

        const xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = () => {
            if (xhttp.readyState == 4) {
                if (xhttp.status == 200) {
                    const json = this.fixGeoJson_(xhttp.responseText)
                    this.geoJSON = json;
                    this.activeUrl_ = url;
                    this.applyGeoJson_();
                }
                this.activeRequest_ = null;
                this.fire("load");
            }
        };
        xhttp.open("GET", url, true);
        xhttp.send();

        this.activeRequest_ = xhttp;
    }

    /**
     * Internal method to apply the current geojson to the active mapLayer
     * 
     * @private
     */
    applyGeoJson_() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.clearLayers();
                this.mapLayers_.leaflet.addData(this.geoJSON);
                break;
        }
    }

    /**
     * Apply a filter on the layer. Uses WMS filter string
     */
    applyFilter() {
        // only apply filter if the layer is active
        if (this.active) {
            const url = this.createUrl_();
            if (this.activeUrl_ != url) {
                this.loadGeojson_(url);
            }
        }
    }

    /**
     * Get Info of the last clicked features
     * @returns {Promise} feature info
     */
    getFeatureInfo() {
        return FeatureLayerUtils.getFeatureInfo(this);
    }

    /**
     * Set the transparency between 0 and 1
     * @override
     * @param {number} opacity
     */
    setOpacity(opacity) {
        FeatureLayerUtils.setOpacity(this, opacity);
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        const bounds = FeatureLayerUtils.computeBounds(this);
        return (bounds) ? bounds : super.getBounds();
    }

}